import {
  Add as AddIcon,
  ArrowDownward,
  ArrowUpward,
  DeleteOutline,
  OpenInNew,
  PlaceOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { CountedField, MarginaliaField } from './fields';
import {
  chapterPath,
  EditorGuide,
  findChapter,
  findPick,
  LIMITS,
  ledeParagraphPath,
  matchPickVenue,
  MAX_META,
  MAX_PARAGRAPHS,
  MIN_LEDE_PARAGRAPHS,
  ParagraphTarget,
  pickCaption,
  pickHasCaption,
  pickMetaPath,
  pickNeedsCopy,
  pickParagraphPath,
  pickPath,
  Selection,
  titleAccent,
  titleText,
} from './model';
import { mapsLink } from './urls';

/** What the forms need from the editor screen. */
export interface FormApi {
  /** The guide with the queued edits applied. */
  draft: EditorGuide;
  /** The guide as the server last returned it (venues only change through the server). */
  base: EditorGuide;
  readOnly: boolean;
  unaddressable: Set<string>;
  setText: (path: string, value: string) => void;
  setChapterKicker: (chapterId: string, kicker: string) => void;
  insertParagraph: (target: ParagraphTarget, index: number) => void;
  removeParagraph: (target: ParagraphTarget, index: number) => void;
  moveParagraph: (target: ParagraphTarget, from: number, to: number) => void;
  removeMeta: (pickId: string, index: number) => void;
  movePick: (pickId: string, chapterId: string, index: number) => void;
  removePick: (pickId: string) => void;
  moveChapter: (chapterId: string, index: number) => void;
  removeChapter: (chapterId: string) => void;
  /** The problem on a field (client check or backend refusal), by field key. */
  errorFor: (key: string) => string | undefined;
  openAddPlace: (chapterId: string, index?: number) => void;
  openChangePlace: (pickId: string) => void;
  select: (s: Selection) => void;
}

const LEDE: ParagraphTarget = { target: 'lede' };

function Section({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      {title && (
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          {title}
        </Typography>
      )}
      <Stack spacing={2}>{children}</Stack>
    </Paper>
  );
}

// ─── the guide ───────────────────────────────────────────────────────────────

export function GuideForm({ api }: { api: FormApi }) {
  const { draft, readOnly, setText, errorFor } = api;
  const title = titleText(draft.title);
  const accent = titleAccent(draft.title);
  const accentProblem =
    accent && !title.includes(accent) ? 'The accent must appear in the title.' : undefined;
  const ledeParagraphs = draft.lede?.paragraphs ?? [];

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Guide
      </Typography>
      <Section title="Title">
        <CountedField
          label="Title"
          required
          value={title}
          limit={LIMITS['title.text']}
          onChange={(v) => setText('title.text', v)}
          disabled={readOnly}
          error={errorFor('title.text')}
          testId="field-title"
        />
        <CountedField
          label="Title accent (the italic part)"
          value={accent}
          limit={LIMITS['title.accent']}
          onChange={(v) => setText('title.accent', v)}
          disabled={readOnly}
          error={accentProblem || errorFor('title.accent')}
          help="Must be a word or phrase from the title. Leave blank for none."
          testId="field-accent"
        />
        <CountedField
          label="Dek"
          multiline
          minRows={2}
          value={draft.dek ?? ''}
          limit={LIMITS.dek}
          onChange={(v) => setText('dek', v)}
          disabled={readOnly}
          error={errorFor('dek')}
        />
      </Section>
      <Section title="Lede">
        <CountedField
          label="Lede headline"
          required
          value={titleText(draft.lede?.headline)}
          limit={LIMITS['lede.headline']}
          onChange={(v) => setText('lede.headline', v)}
          disabled={readOnly || !draft.lede}
          error={errorFor('lede.headline')}
        />
        {ledeParagraphs.map((p, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <CountedField
                label={`Lede paragraph ${i + 1}`}
                multiline
                minRows={3}
                value={p}
                limit={LIMITS['lede.paragraph']}
                onChange={(v) => setText(ledeParagraphPath(i), v)}
                disabled={readOnly}
                error={errorFor(ledeParagraphPath(i))}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <IconButton
                size="small"
                aria-label={`Move lede paragraph ${i + 1} up`}
                disabled={readOnly || i === 0}
                onClick={() => api.moveParagraph(LEDE, i, i - 1)}
              >
                <ArrowUpward fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label={`Move lede paragraph ${i + 1} down`}
                disabled={readOnly || i === ledeParagraphs.length - 1}
                onClick={() => api.moveParagraph(LEDE, i, i + 1)}
              >
                <ArrowDownward fontSize="small" />
              </IconButton>
              <Tooltip title={ledeParagraphs.length <= MIN_LEDE_PARAGRAPHS ? 'The lede keeps at least one paragraph.' : ''}>
                <span>
                  <IconButton
                    size="small"
                    aria-label={`Remove lede paragraph ${i + 1}`}
                    disabled={readOnly || ledeParagraphs.length <= MIN_LEDE_PARAGRAPHS}
                    onClick={() => api.removeParagraph(LEDE, i)}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        ))}
        {errorFor('lede.paragraphs') && <Alert severity="error">{errorFor('lede.paragraphs')}</Alert>}
        <Box>
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={readOnly || !draft.lede || ledeParagraphs.length >= MAX_PARAGRAPHS}
            onClick={() => api.insertParagraph(LEDE, ledeParagraphs.length)}
          >
            Add lede paragraph
          </Button>
          {ledeParagraphs.length >= MAX_PARAGRAPHS && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              At most {MAX_PARAGRAPHS} paragraphs.
            </Typography>
          )}
        </Box>
      </Section>
      <Section title="Search">
        <CountedField
          label="SEO title"
          value={draft.seo?.title ?? ''}
          limit={LIMITS['seo.title']}
          onChange={(v) => setText('seo.title', v)}
          disabled={readOnly}
          error={errorFor('seo.title')}
        />
        <CountedField
          label="SEO description"
          multiline
          minRows={2}
          value={draft.seo?.description ?? ''}
          limit={LIMITS['seo.description']}
          onChange={(v) => setText('seo.description', v)}
          disabled={readOnly}
          error={errorFor('seo.description')}
        />
      </Section>
    </Box>
  );
}

// ─── a chapter ───────────────────────────────────────────────────────────────

export function ChapterForm({ api, chapterId }: { api: FormApi; chapterId: string }) {
  const { draft, readOnly, errorFor } = api;
  const found = findChapter(draft, chapterId);
  if (!found) return <Alert severity="info">This chapter is no longer in the guide.</Alert>;
  const { chapter, index } = found;
  const picks = chapter.picks ?? [];
  const chapterCount = draft.chapters?.length ?? 0;
  const empty = picks.length === 0;
  const removable = empty && chapterCount > 1;
  const removeReason = !empty
    ? 'Move or remove its places first: only an empty chapter can be removed.'
    : chapterCount <= 1
    ? 'A guide needs at least one chapter.'
    : '';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
          Chapter
        </Typography>
        <Tooltip title="Move up">
          <span>
            <IconButton
              size="small"
              disabled={readOnly || index === 0}
              onClick={() => api.moveChapter(chapterId, index - 1)}
              aria-label="Move chapter up"
            >
              <ArrowUpward fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Move down">
          <span>
            <IconButton
              size="small"
              disabled={readOnly || index >= chapterCount - 1}
              onClick={() => api.moveChapter(chapterId, index + 1)}
              aria-label="Move chapter down"
            >
              <ArrowDownward fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Section>
        <CountedField
          label="Chapter title"
          required
          value={titleText(chapter.title)}
          limit={LIMITS['chapter.title']}
          onChange={(v) => api.setText(chapterPath(chapterId, 'title'), v)}
          disabled={readOnly}
          error={errorFor(chapterPath(chapterId, 'title'))}
        />
        <CountedField
          label="Kicker (optional)"
          value={chapter.kicker ?? ''}
          limit={LIMITS['chapter.kicker']}
          onChange={(v) => api.setChapterKicker(chapterId, v)}
          disabled={readOnly}
          error={errorFor(`chapters[${chapterId}].kicker`)}
          help="The short label in the index and the chapter strip."
        />
        <CountedField
          label="Dek"
          multiline
          minRows={2}
          value={chapter.dek ?? ''}
          limit={LIMITS['chapter.dek']}
          onChange={(v) => api.setText(chapterPath(chapterId, 'dek'), v)}
          disabled={readOnly}
          error={errorFor(chapterPath(chapterId, 'dek'))}
        />
      </Section>
      {errorFor(`chapters[${chapterId}]`) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorFor(`chapters[${chapterId}]`)}
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          disabled={readOnly}
          onClick={() => api.openAddPlace(chapterId)}
        >
          Add place
        </Button>
        <Tooltip title={removable ? '' : removeReason}>
          <span>
            <Button
              color="error"
              variant="outlined"
              startIcon={<DeleteOutline />}
              disabled={readOnly || !removable}
              onClick={() => api.removeChapter(chapterId)}
            >
              Remove chapter
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}

// ─── a pick ──────────────────────────────────────────────────────────────────

export function PickForm({ api, pickId }: { api: FormApi; pickId: string }) {
  const { draft, base, errorFor } = api;
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [addingMeta, setAddingMeta] = useState(false);
  const found = findPick(draft, pickId);
  if (!found) return <Alert severity="info">This place is no longer in the guide.</Alert>;
  const { pick, chapter, index } = found;
  const locked = api.unaddressable.has(pickId);
  const readOnly = api.readOnly || locked;
  const chapterPicks = chapter.picks ?? [];
  const paragraphs = pick.paragraphs ?? [];
  const meta = pick.meta ?? [];
  const needsCopy = pickNeedsCopy(pick);

  // The linked place: venues only change through the server, so read them from the base.
  const baseFound = findPick(base, pickId);
  const venue = baseFound ? matchPickVenue(baseFound.pick, baseFound.chapter) : null;
  const venueName = venue ? titleText(venue.name) : '';

  const metaRows = addingMeta && meta.length < MAX_META ? meta.length + 1 : meta.length;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
          Place
        </Typography>
        <Tooltip title="Move up in this chapter">
          <span>
            <IconButton
              size="small"
              disabled={readOnly || index === 0}
              onClick={() => api.movePick(pickId, chapter.id, index - 1)}
              aria-label="Move place up"
            >
              <ArrowUpward fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Move down in this chapter">
          <span>
            <IconButton
              size="small"
              disabled={readOnly || index >= chapterPicks.length - 1}
              onClick={() => api.movePick(pickId, chapter.id, index + 1)}
              aria-label="Move place down"
            >
              <ArrowDownward fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      {locked && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This place shares its id with another place, so it cannot be edited here. The editor
          addresses places by id.
        </Alert>
      )}
      {needsCopy && (
        <Chip
          color="warning"
          size="small"
          label="Needs copy before it can publish"
          sx={{ mb: 2 }}
          data-testid="needs-copy-chip"
        />
      )}
      {errorFor(`picks[${pickId}]`) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorFor(`picks[${pickId}]`)}
        </Alert>
      )}

      <Section title="Card">
        <CountedField
          label="Title"
          required
          value={titleText(pick.title)}
          limit={LIMITS['pick.title']}
          onChange={(v) => api.setText(pickPath(pickId, 'title'), v)}
          disabled={readOnly}
          error={errorFor(pickPath(pickId, 'title'))}
          testId="field-pick-title"
        />
        <CountedField
          label="Kicker (neighbourhood, category)"
          value={pick.kicker ?? ''}
          limit={LIMITS['pick.kicker']}
          onChange={(v) => api.setText(pickPath(pickId, 'kicker'), v)}
          disabled={readOnly}
          error={errorFor(pickPath(pickId, 'kicker'))}
        />
      </Section>

      <Section title="Meta lines">
        {Array.from({ length: metaRows }).map((_, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <CountedField
                label={`Meta line ${i + 1}`}
                required
                value={meta[i] ?? ''}
                limit={LIMITS['pick.meta']}
                onChange={(v) => {
                  api.setText(pickMetaPath(pickId, i), v);
                  if (v.trim()) setAddingMeta(false);
                }}
                disabled={readOnly}
                error={errorFor(pickMetaPath(pickId, i))}
              />
            </Box>
            <IconButton
              size="small"
              aria-label={`Remove meta line ${i + 1}`}
              disabled={readOnly}
              onClick={() => (i < meta.length ? api.removeMeta(pickId, i) : setAddingMeta(false))}
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Box>
        ))}
        {errorFor(`picks[${pickId}].meta`) && (
          <Alert severity="error">{errorFor(`picks[${pickId}].meta`)}</Alert>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={readOnly || addingMeta || meta.length >= MAX_META}
            onClick={() => setAddingMeta(true)}
          >
            Add meta line
          </Button>
          <Typography variant="caption" color="text.secondary">
            Up to {MAX_META}.
          </Typography>
        </Box>
      </Section>

      <Section title="Body">
        {paragraphs.map((p, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <CountedField
                label={`Paragraph ${i + 1}`}
                multiline
                minRows={3}
                value={p}
                limit={LIMITS['pick.paragraph']}
                onChange={(v) => api.setText(pickParagraphPath(pickId, i), v)}
                disabled={readOnly}
                error={errorFor(pickParagraphPath(pickId, i))}
                testId={`field-paragraph-${i}`}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <IconButton
                size="small"
                aria-label={`Move paragraph ${i + 1} up`}
                disabled={readOnly || i === 0}
                onClick={() => api.moveParagraph({ pickId }, i, i - 1)}
              >
                <ArrowUpward fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label={`Move paragraph ${i + 1} down`}
                disabled={readOnly || i === paragraphs.length - 1}
                onClick={() => api.moveParagraph({ pickId }, i, i + 1)}
              >
                <ArrowDownward fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label={`Remove paragraph ${i + 1}`}
                disabled={readOnly}
                onClick={() => api.removeParagraph({ pickId }, i)}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ))}
        {errorFor(`picks[${pickId}].paragraphs`) && (
          <Alert severity="error">{errorFor(`picks[${pickId}].paragraphs`)}</Alert>
        )}
        <Box>
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={readOnly || paragraphs.length >= MAX_PARAGRAPHS}
            onClick={() => api.insertParagraph({ pickId }, paragraphs.length)}
          >
            Add paragraph
          </Button>
          {paragraphs.length >= MAX_PARAGRAPHS && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              At most {MAX_PARAGRAPHS} paragraphs.
            </Typography>
          )}
        </Box>
      </Section>

      <Section title="Photo and note">
        {pickHasCaption(pick) ? (
          <CountedField
            label="Caption"
            value={pickCaption(pick)}
            limit={LIMITS['pick.caption']}
            onChange={(v) => api.setText(pickPath(pickId, 'caption'), v)}
            disabled={readOnly}
            error={errorFor(pickPath(pickId, 'caption'))}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            This place shows no photo, so there is no caption to edit.
          </Typography>
        )}
        {pick.type === 'text' ? (
          <MarginaliaField
            value={pick.marginalia ?? ''}
            limit={LIMITS['pick.marginalia']}
            onChange={(v) => api.setText(pickPath(pickId, 'marginalia'), v)}
            disabled={readOnly}
            error={errorFor(pickPath(pickId, 'marginalia'))}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            Only a text card shows an editor's note.
          </Typography>
        )}
      </Section>

      <Section title="Linked place">
        {venue ? (
          <Box data-testid="linked-place">
            <Typography variant="body2" fontWeight={600}>
              {venueName || titleText(pick.title)}
            </Typography>
            {venue.address && (
              <Typography variant="body2" color="text.secondary">
                {venue.address}
              </Typography>
            )}
            <Link
              href={mapsLink(venueName || titleText(pick.title), venue.address, venue.placeId)}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
            >
              Open in Google Maps <OpenInNew sx={{ fontSize: 14 }} />
            </Link>
            {venue.businessStatus && venue.businessStatus !== 'OPERATIONAL' && (
              <Chip size="small" color="warning" label={venue.businessStatus} sx={{ ml: 1 }} />
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            This card is not linked to a place on the map.
          </Typography>
        )}
        <Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PlaceOutlined />}
            disabled={readOnly}
            onClick={() => api.openChangePlace(pickId)}
          >
            Change place…
          </Button>
        </Box>
      </Section>

      <Button
        color="error"
        variant="outlined"
        startIcon={<DeleteOutline />}
        disabled={readOnly}
        onClick={() => setConfirmRemove(true)}
      >
        Remove from guide
      </Button>

      <Dialog open={confirmRemove} onClose={() => setConfirmRemove(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove this place?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{titleText(pick.title) || pickId}" and its map pin come out of the guide when you
            save. Discarding your unsaved changes brings it back.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemove(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setConfirmRemove(false);
              api.removePick(pickId);
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
